import React from "react";
import { useRouter } from "next/navigation";
import { Play } from "lucide-react";

interface Media {
  url: string;
  type: string;
}

interface MediaGridProps {
  medias: Media[];
  postId: string; // Changed from number to string for UUIDs
}

export function MediaGrid({ medias, postId }: MediaGridProps) {
  const router = useRouter();

  if (!medias || !Array.isArray(medias) || medias.length === 0) return null;

  const isVideo = (type: string) => type.startsWith('video/');

  const getGridClassName = (count: number) => {
    switch (count) {
      case 1:
        return 'grid-cols-1';
      case 2:
        return 'grid-cols-2 gap-0.5';
      case 3:
        return 'grid-cols-2 gap-0.5';
      case 4:
        return 'grid-cols-2 gap-0.5';
      default:
        return 'grid-cols-2 gap-0.5';
    }
  };

  const getImageClassName = (index: number, total: number) => {
    if (total === 1) {
      return 'rounded-2xl w-full h-auto max-h-[510px] object-contain bg-black';
    }
    
    if (total === 2) {
      return 'rounded-2xl w-full h-[250px] object-cover';
    }
    
    if (total === 3) {
      if (index === 0) {
        return 'rounded-2xl w-full h-[250px] object-cover';
      }
      return 'rounded-2xl w-full h-[124px] object-cover';
    }
    
    if (total === 4) {
      return 'rounded-2xl w-full h-[124px] object-cover';
    }

    return 'rounded-2xl w-full h-[124px] object-cover';
  };

  return (
    <div 
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        router.replace(`/post/${postId}/photo`);
      }}
      className={`grid mt-2 cursor-pointer ${getGridClassName(medias.length)}`}
    >
      {Array.isArray(medias) && medias.slice(0, 4).map((media, index) => (
        <div key={index} className={`relative ${
          medias.length === 3 && index === 0 ? 'row-span-2' : ''
        }`}>
          {isVideo(media.type) ? (
            <div className="relative w-full h-full">
              <video
                src={media.url}
                className={getImageClassName(index, medias.length)}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <Play className="w-8 h-8 text-white bg-black/50 rounded-full p-1" />
              </div>
            </div>
          ) : (
            <img
              src={media.url}
              alt={`Post media ${index + 1}`}
              className={getImageClassName(index, medias.length)}
            />
          )}
          {index === 3 && medias.length > 4 && (
            <div className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center">
              <span className="text-white text-xl font-bold">+{medias.length - 4}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}